import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '../RequestAction';
import { ArsenalUnitMapper } from '@Mapper/arsenal/arsenal-unit.mapper';
import { Nullable, ArsenalUnit } from '@Models';
import { RequestArsenalUnitSuccess } from '@Repository/arsenal';
import { ApiClient } from '@Api';
import { Locale } from '../locale';

@injectable()
export class ArsenalUnitRepository {
  @inject(TYPES.Mapper.ArsenalUnitMapper)
  private _arsenalUnitMapper!: ArsenalUnitMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;
  @inject(TYPES.Locale) private _locale!: Locale;

  public async create(
    arsenalId: string,
    arsenalUnit: ArsenalUnit,
    {
      locale,
    }: {
      locale: Nullable<string>;
    },
  ): Promise<RequestAction> {
    try {
      const arsenalUnitRequestBody =
        this._arsenalUnitMapper.mapDto(arsenalUnit);

      const arsenalUnitDto = await this._apiClient.arsenal.createUnit({
        id: arsenalId,
        locale: locale ?? this._locale.getlocale(),
        requestBody: arsenalUnitRequestBody,
      });
      return new RequestArsenalUnitSuccess({
        payload: this._arsenalUnitMapper.map(arsenalUnitDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async update(
    arsenalUnit: ArsenalUnit,
    { locale }: { locale: Nullable<string> },
  ): Promise<RequestAction> {
    try {
      const requestBody = this._arsenalUnitMapper.mapDto(arsenalUnit);

      const arsenalUnitDto = await this._apiClient.arsenal.updateUnit({
        locale: locale ?? this._locale.getlocale(),
        id: arsenalUnit.id!!,
        requestBody: requestBody,
      });

      return new RequestArsenalUnitSuccess({
        payload: this._arsenalUnitMapper.map(arsenalUnitDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async delete(id: string): Promise<RequestAction> {
    try {
      await this._apiClient.arsenal.deleteUnit({
        locale: this._locale.getlocale(),
        id: id,
      });
      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }
}
